'use client';
import {
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
  Ref,
  ReactNode,
} from 'react';

import { StyledModal, StylesProps } from './styled';
import { ModalCloseButton } from './utils';

export type ModalProps = {
  isVisible?: boolean;
  onCancel?: () => void;
  children?: ReactNode;
  width?: number | string;
  title?: ReactNode;
  footer?: ReactNode;
  closeIcon?: ReactNode;
  zIndex?: number;
  maskClosable?: boolean;
  closeable?: boolean;
  centered?: boolean;
} & StylesProps;

export type ModalApi = {
  show: () => void;
  hide: () => void;
};

const useModalApi = (ref: Ref<ModalApi>, initialState?: boolean) => {
  const [state, setIsModalVisible] = useState(initialState);
  const show = useCallback(() => setIsModalVisible(true), []);
  const hide = useCallback(() => setIsModalVisible(false), []);
  useImperativeHandle(ref, () => {
    return {
      show,
      hide,
    };
  });
  return { state, hide };
};

export const Modal = forwardRef<ModalApi, ModalProps>(
  (
    {
      children,
      closeIcon,
      isVisible,
      footer = null,
      onCancel,
      closeable = true,
      zIndex = 1000, //default antd modal value
      ...rest
    },
    ref
  ) => {
    const { state, hide } = useModalApi(ref, isVisible);

    return (
      <StyledModal
        open={state}
        onCancel={
          closeable
            ? () => {
                onCancel?.();
                hide();
              }
            : undefined
        }
        zIndex={zIndex}
        footer={footer}
        centered
        closeIcon={
          closeable ? closeIcon || <ModalCloseButton noLabel /> : undefined
        }
        closable={closeable}
        {...rest}
      >
        {children}
      </StyledModal>
    );
  }
);

export default Modal;
